<template>
  <!-- 空间导入弹窗 -->
  <a-modal
    :title="title"
    v-model:visible="visible"
    :confirm-loading="uploading"
    @cancel="importExcelHandleCancel"
    @ok="importExcelHandleChange"
  >
    <!-- 上传加载状态 -->
    <a-spin tip="上传中..." :spinning="uploading">
      <!-- 文件拖拽上传组件 -->
      <a-upload-dragger
        name="file"
        accept=".xlsx, .xls"
        :file-list="fileList"
        :multiple="false"
        :remove="handleRemove"
        :before-upload="beforeUpload"
      >
        <!-- 上传图标 -->
        <p class="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <!-- 上传提示文字 -->
        <p class="ant-upload-text">请将文件拖拽到此处上传</p>
        <!-- 上传说明文字 -->
        <p class="ant-upload-hint">支持单个上传，也可以点击后选择文件上传</p>
      </a-upload-dragger>
      <!-- 下载模板链接 -->
      <!-- <a @click="importTemplate" v-hasPermi="['system:space:importTemplate']"
        >下载模板</a
      > -->
    </a-spin>
  </a-modal>
</template>

<script setup>
import { $emit } from '../../../../utils/gogocodeTransfer'
import { ref } from 'vue'
import { useStore } from 'vuex'
import { message } from 'ant-design-vue'
import { InboxOutlined } from '@ant-design/icons-vue'
/**
 * 空间导入Excel组件
 * 功能：提供空间数据的Excel导入功能
 * - 支持拖拽上传和点击上传
 * - 导入前需要选择项目ID
 * - 导入成功后刷新列表
 */
import { importData } from '@/api/system/space'
import { showImportResult, isImportSuccessCode } from '@/utils/importResult'

const emit = defineEmits(['ok'])
const store = useStore()

// 响应式数据
const title = ref('空间导入')
const visible = ref(false)
const fileList = ref([])
const uploading = ref(false)
const updateSupport = ref(0)

// 方法
/**
 * 取消导入操作
 * 关闭弹窗，清空文件列表，触发ok事件刷新列表
 */
const importExcelHandleCancel = () => {
  visible.value = false
  fileList.value = []
  // 关闭后刷新列表
  emit('ok')
}

/**
 * 打开导入弹窗
 * 在打开弹窗前检查项目ID是否已选择
 */
const importExcelHandleOpen = () => {
  // 检查项目ID是否已选择
  const currentProjectId = store.getters.currentProjectId
  if (!currentProjectId) {
    message.warning('请先选择项目')
    return
  }
  // 打开弹窗
  visible.value = true
}

/**
 * 文件上传前的处理
 * 阻止自动上传，手动控制上传流程
 * @param {File} file - 选择的文件对象
 * @returns {Boolean} false-阻止自动上传
 */
const beforeUpload = (file) => {
  // 将文件添加到文件列表
  fileList.value = [file]
  // 返回false阻止自动上传，由手动控制
  return false
}

/**
 * 执行导入操作
 * 1. 检查项目ID是否已选择
 * 2. 检查是否选择了文件
 * 3. 构建FormData并发送导入请求
 * 4. 处理导入结果
 */
const importExcelHandleChange = async () => {
  // 1. 检查项目ID是否已选择
  const currentProjectId = store.getters.currentProjectId
  if (!currentProjectId) {
    message.warning('请先选择项目')
    return
  }

  // 2. 检查是否选择了文件
  if (!fileList.value || fileList.value.length === 0) {
    message.warning('请先选择要导入的文件')
    return
  }

  // 3. 开始上传，显示加载状态
  uploading.value = true

  try {
    // 4. 构建FormData表单数据
    const formData = new FormData()
    formData.append('file', fileList.value[0]) // 上传的文件
    formData.append('updateSupport', updateSupport.value) // 是否支持更新
    formData.append('projectId', currentProjectId) // 项目ID

    // 5. 发送导入请求
    const response = await importData(formData)
    // 导入成功
    if (isImportSuccessCode(response.code)) {
      // 清空文件列表
      fileList.value = []
      // 显示导入明细
      showImportResult(response)
      // 关闭弹窗
      visible.value = false
      // 触发ok事件，刷新列表
      emit('ok')
    } else {
      // 导入失败，显示错误信息
      message.error(response.message)
    }
  } catch (error) {
    console.error('导入失败:', error)
    message.error('导入失败，请稍后重试')
  } finally {
    // 无论成功或失败，都要关闭加载状态
    uploading.value = false
  }
}

/**
 * 移除文件
 * 清空文件列表，关闭上传状态
 */
const handleRemove = () => {
  fileList.value = []
  uploading.value = false
}

// 暴露方法给父组件
defineExpose({
  importExcelHandleOpen,
})
</script>

<style>
/* Modal 按钮样式 - 参照 CreateForm.vue */
/* 取消按钮样式 */
.ant-modal-footer .ant-btn-default {
  background-color: #F3F3F3 !important;
  border-color: #F3F3F3 !important;
  color: #101010 !important;
  border-radius: 4px !important;
}

.ant-modal-footer .ant-btn-default:hover {
  background-color: #F3F3F3 !important;
  border-color: #F3F3F3 !important;
  color: #101010 !important;
}

.ant-modal-footer .ant-btn-default:focus {
  background-color: #F3F3F3 !important;
  border-color: #F3F3F3 !important;
  color: #101010 !important;
}

/* 确定按钮样式 */
.ant-modal-footer .ant-btn-primary {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
  border-radius: 4px !important;
}

.ant-modal-footer .ant-btn-primary:hover:not(:disabled) {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
  opacity: 0.9;
}

.ant-modal-footer .ant-btn-primary:focus:not(:disabled) {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
}

.ant-modal-footer .ant-btn-primary:active:not(:disabled) {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
}

/* 按钮保持圆角 */
.ant-modal-footer .ant-btn {
  border-radius: 4px !important;
}
</style>
